import React from 'react';
import { Box, IconButton, MenuItem, Select, Skeleton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DataCell, DataRow } from '../Tables/DataList';
import { countSummary, pageWindow, PER_PAGE_OPTIONS, totalPages } from './paginationUtils';

export { DEFAULT_PER_PAGE } from './paginationUtils';

/**
 * Skeleton placeholder rows shown while a tab's page is loading.
 * `columns` is the list of `xs` spans (matching the tab's real columns).
 */
export function TabSkeletonRows(props: { rows: number; columns: number[] }) {
  const { rows, columns } = props;
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <DataRow key={`skeleton-${rowIndex}`}>
          {columns.map((xs, columnIndex) => (
            <DataCell key={columnIndex} xs={xs}>
              <Skeleton variant='text' />
            </DataCell>
          ))}
        </DataRow>
      ))}
    </>
  );
}

export function TabEmpty(props: { label?: string }) {
  return (
    <Box sx={{ py: 4, textAlign: 'center' }}>
      <Typography variant='body2' color='text.secondary'>{props.label ?? 'No records'}</Typography>
    </Box>
  );
}

const PaginationFooter = styled('div', { shouldForwardProp: (prop) => prop !== 'inline' })<{ inline?: boolean }>(
  ({ inline }) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    ...(inline
      ? { borderTop: '1px solid rgba(0, 0, 0, 0.08)', paddingTop: 12, marginTop: 8 }
      : { flexShrink: 0, height: 56, borderTop: '1px solid rgba(0, 0, 0, 0.1)', background: '#fff', paddingLeft: 24, paddingRight: 24 }),
  }),
);

const PagerLeft = styled('div')({ display: 'flex', alignItems: 'center', gap: 16 });
const PerPageLabel = styled('span')({ fontSize: 13, color: '#5a6472' });
const CountLabel = styled('span')({ fontSize: 14, color: '#5a6472' });
const PerPageSelect = styled(Select)({ height: 34, fontSize: 14, backgroundColor: '#fff' });
const PagerNav = styled('div')({ display: 'flex', alignItems: 'center', gap: 4, position: 'absolute', left: '50%', transform: 'translateX(-50%)' });

const NavButton = styled(IconButton)({ color: '#37424f', width: 32, height: 32, '&.Mui-disabled': { opacity: 0.35 } });

const PageButton = styled(IconButton, { shouldForwardProp: (prop) => prop !== 'active' })<{ active?: boolean }>(
  ({ active }) => ({
    minWidth: 32,
    height: 32,
    borderRadius: '50%',
    fontSize: 14,
    ...(active ? { background: '#1976d2', color: '#fff', '&:hover': { background: '#135ea7' } } : { color: '#37424f' }),
  }),
);

/**
 * Bottom-pinned (or inline) pagination bar for a company-detail section: a
 * per-page selector (10/15/25/50, default 10), a result-count summary, and
 * first / previous / numbered / next / last controls. Always renders — a
 * single-page section shows the count with the nav controls inactive.
 */
export function PaginationBar(props: {
  count: number;
  page: number;
  pageSize: number;
  onPage: (page: number) => void;
  onPageSize: (pageSize: number) => void;
  variant?: 'pinned' | 'inline';
  perPageOptions?: number[];
}) {
  const { count, page, pageSize, onPage, onPageSize, variant = 'pinned', perPageOptions } = props;
  // Keep the current pageSize selectable even if a caller passes options that omit it.
  const options = (perPageOptions ?? PER_PAGE_OPTIONS).includes(pageSize)
    ? (perPageOptions ?? PER_PAGE_OPTIONS)
    : [...(perPageOptions ?? PER_PAGE_OPTIONS), pageSize].sort((a, b) => a - b);
  const pages = totalPages(count, pageSize);
  const atFirst = page <= 1;
  const atLast = page >= pages;

  return (
    <PaginationFooter inline={variant === 'inline'}>
      <PagerLeft>
        <PerPageLabel>Per Page</PerPageLabel>
        <PerPageSelect size='small' value={pageSize} onChange={(e) => onPageSize(Number(e.target.value))}>
          {options.map((opt) => (<MenuItem key={opt} value={opt}>{opt}</MenuItem>))}
        </PerPageSelect>
        <CountLabel>{countSummary(page, pageSize, count)}</CountLabel>
      </PagerLeft>
      <PagerNav>
        <NavButton aria-label='First page' size='small' disabled={atFirst} onClick={() => onPage(1)}><FirstPageIcon /></NavButton>
        <NavButton aria-label='Previous page' size='small' disabled={atFirst} onClick={() => onPage(page - 1)}><ChevronLeftIcon /></NavButton>
        {pageWindow(page, pages).map((n) => (
          <PageButton key={n} active={n === page} aria-label={`Page ${n}`} aria-current={n === page ? 'page' : undefined} size='small' onClick={() => onPage(n)}>{n}</PageButton>
        ))}
        <NavButton aria-label='Next page' size='small' disabled={atLast} onClick={() => onPage(page + 1)}><ChevronRightIcon /></NavButton>
        <NavButton aria-label='Last page' size='small' disabled={atLast} onClick={() => onPage(pages)}><LastPageIcon /></NavButton>
      </PagerNav>
    </PaginationFooter>
  );
}
